import type { EquipmentConfig } from '../AppearanceDefinitions';
import type { ItemEquipmentVisualDescriptor } from '../../../shared/equipmentVisuals';
import type { EquipmentSchemaSlot } from '../../../shared/equipmentSlots';
import {
  bodyRigForGear,
  DEFAULT_GEAR_FIT,
  EMPTY_EQUIPMENT_SLOTS,
  GEAR_COLORS,
  darken,
} from './GearGeometry';
import type { GearFitContext } from './GearGeometry';
import {
  cloakRecipe,
  headRecipe,
  legRecipe,
  neckRecipe,
  shoulderRecipe,
  sleeveRecipe,
  torsoRecipe,
  type GearCoverageGeometry,
  type HeadFamily,
  type LegFamily,
  type NeckFamily,
  type TorsoFamily,
  type TorsoRecipeGeometry,
} from './GearRecipes';
import { flameHeadFamilies } from './flameHeadFamilies';
import type { BodyRig } from './HumanoidGeometry';
import type { GhostPoseAnchors } from './AttachmentGeometry';

export interface GearSilhouetteLayers {
  back: string;
  body: string;
  head: string;
}

export interface GearSleeveLayers {
  back: string;
  front: string;
}

type EquipmentSlots = EquipmentConfig['slots'];

interface MaterialLayers {
  back: string[];
  body: string[];
}

const NON_SCALING_STROKE = 'vector-effect="non-scaling-stroke"';

function path(
  d: string,
  fill: string,
  stroke = 'none',
  strokeWidth = 0,
  opacity = 1,
  attrs = '',
): string {
  return `<path ${attrs} d="${d}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" opacity="${opacity}" ${NON_SCALING_STROKE} />`;
}

function fitAttrs(
  recipe: string,
  context: GearFitContext,
  fitMode: string,
  variantTier: number,
): string {
  return `data-gear-recipe="${recipe}" data-gear-shape="${context.shape}" data-gear-fit-mode="${fitMode}" data-gear-variant-tier="${variantTier}"`;
}

function coverageAttrs(coverage: GearCoverageGeometry): string {
  return `data-gear-coverage-region="${coverage.region}" data-gear-coverage-mode="${coverage.mode}" data-gear-coverage-left="${coverage.left}" data-gear-coverage-right="${coverage.right}" data-gear-coverage-top="${coverage.topY}" data-gear-coverage-bottom="${coverage.bottomY}"`;
}

function renderBodyWrapCoverage(
  coverage: GearCoverageGeometry,
  color: string,
  outline: string,
  strokeWidth: number,
): string {
  return `<g ${coverageAttrs(coverage)}>
    <path data-gear-coverage-silhouette="${coverage.region}" d="${coverage.path}" fill="${color}" stroke="${outline}" stroke-width="${strokeWidth}" stroke-linejoin="round" />
  </g>`;
}

function renderHeadWrapCoverage(
  coverage: GearCoverageGeometry,
  color: string,
  outline: string,
): string {
  return `<g ${coverageAttrs(coverage)}>
    <path data-gear-coverage-silhouette="head" d="${coverage.path}" fill="${color}" fill-rule="evenodd" clip-rule="evenodd" stroke="${outline}" stroke-width="2.6" stroke-linejoin="round" />
  </g>`;
}

function equipAttrs(d: ItemEquipmentVisualDescriptor): string {
  return `data-equipment-slot="${d.schemaSlot}" data-equipment-item-id="${d.itemId}" data-equipment-family="${d.family}" data-equipment-variant="${d.variant}"`;
}

function variantAccent(
  geometry: TorsoRecipeGeometry,
  color: string,
): string {
  const tier = geometry.variantTier;
  if (tier <= 0) return '';

  const center = geometry.centerX;
  const upper = geometry.topY + 12;
  const lower = geometry.hemY - 8;
  const width = Math.max(5, Math.min(15, (geometry.right - geometry.left) * 0.16));

  if (tier === 1) {
    return `<path data-gear-variant-accent="seam" d="M${center},${upper} L${center},${lower}" fill="none" stroke="${color}" stroke-width="1.2" opacity="0.7" ${NON_SCALING_STROKE} />`;
  }
  if (tier === 2) {
    return `<path data-gear-variant-accent="chevron" d="M${center - width},${upper} L${center},${upper + 7} L${center + width},${upper}" fill="none" stroke="${color}" stroke-width="1.5" opacity="0.76" ${NON_SCALING_STROKE} />`;
  }
  if (tier === 3) {
    return `<circle data-gear-variant-accent="emblem" cx="${center}" cy="${upper + 5}" r="4.2" fill="${color}" opacity="0.82" ${NON_SCALING_STROKE} />`;
  }
  if (tier === 4) {
    return `<g data-gear-variant-accent="double-chevron" fill="none" stroke="${color}" stroke-width="1.6" opacity="0.82" ${NON_SCALING_STROKE}><path d="M${center - width},${upper} L${center},${upper + 7} L${center + width},${upper}" /><path d="M${center - width + 2},${upper + 7} L${center},${upper + 14} L${center + width - 2},${upper + 7}" /></g>`;
  }
  return `<g data-gear-variant-accent="ornate" fill="none" stroke="${color}" stroke-width="1.5" opacity="0.86" ${NON_SCALING_STROKE}><circle cx="${center}" cy="${upper + 5}" r="4.5" /><path d="M${center - width},${upper + 13} Q${center},${upper + 20} ${center + width},${upper + 13}" /></g>`;
}

function renderTorsoFamily(
  family: TorsoFamily,
  primaryColor: string,
  accentColor: string,
  variant: number,
  context: GearFitContext,
  semanticAttrs = '',
): string {
  const rig = bodyRigForGear(context);
  const geometry = torsoRecipe(rig, family, variant);
  const attrs = `${semanticAttrs} ${fitAttrs(family, context, geometry.fitMode, geometry.variantTier)}`.trim();
  const outline = family === 'plate'
    ? GEAR_COLORS.plateDarkOutline
    : family === 'tunic' || family === 'vest'
      ? GEAR_COLORS.leatherOutline
      : GEAR_COLORS.outline;

  const coverage = renderBodyWrapCoverage(
    geometry.coverage,
    darken(primaryColor, family === 'plate' ? 0.72 : 0.82),
    outline,
    family === 'plate' ? 1.7 : 1.35,
  );
  const shell = path(geometry.shell, primaryColor, outline, family === 'plate' ? 1.6 : 1.25);
  let details = '';

  switch (family) {
  case 'plate':
    details = path(geometry.inset, darken(primaryColor, 0.7), outline, 0.8)
        + `<path d="${geometry.neckline}" fill="none" stroke="${accentColor}" stroke-width="2" opacity="0.88" ${NON_SCALING_STROKE} />`;
    break;
  case 'mail': {
    const dotY = geometry.topY + 14;
    details = `<g fill="${accentColor}" opacity="0.68">`
        + `<circle cx="${geometry.centerX - 12}" cy="${dotY}" r="1.4" />`
        + `<circle cx="${geometry.centerX}" cy="${dotY + 4}" r="1.4" />`
        + `<circle cx="${geometry.centerX + 12}" cy="${dotY}" r="1.4" />`
        + `<circle cx="${geometry.centerX - 7}" cy="${dotY + 13}" r="1.4" />`
        + `<circle cx="${geometry.centerX + 7}" cy="${dotY + 13}" r="1.4" /></g>`;
    break;
  }
  case 'robe':
    details = `<path d="${geometry.neckline}" fill="none" stroke="${accentColor}" stroke-width="2.2" opacity="0.9" ${NON_SCALING_STROKE} />`
        + `<path d="M${geometry.centerX},${geometry.topY + 11} Q${geometry.centerX - 4},${(geometry.topY + geometry.hemY) / 2} ${geometry.centerX},${geometry.hemY - 5}" fill="none" stroke="${accentColor}" stroke-width="2.4" opacity="0.68" ${NON_SCALING_STROKE} />`;
    break;
  case 'tunic':
    details = `<path d="${geometry.frontStrap}" fill="none" stroke="${GEAR_COLORS.leatherStrap}" stroke-width="2.3" opacity="0.92" ${NON_SCALING_STROKE} />`
        + `<path d="${geometry.hem}" fill="none" stroke="${accentColor}" stroke-width="2.4" opacity="0.78" ${NON_SCALING_STROKE} />`;
    break;
  case 'vest':
    details = `<path d="${geometry.neckline}" fill="none" stroke="${accentColor}" stroke-width="1.8" opacity="0.86" ${NON_SCALING_STROKE} />`
        + `<path d="M${geometry.centerX},${geometry.topY + 10} L${geometry.centerX},${geometry.hemY - 4}" fill="none" stroke="${accentColor}" stroke-width="1.5" opacity="0.74" ${NON_SCALING_STROKE} />`;
    break;
  case 'shirt':
    details = `<path d="${geometry.neckline}" fill="none" stroke="${accentColor}" stroke-width="1.5" opacity="0.72" ${NON_SCALING_STROKE} />`
        + `<path d="${geometry.hem}" fill="none" stroke="${accentColor}" stroke-width="1.3" opacity="0.52" ${NON_SCALING_STROKE} />`;
    break;
  }

  return `<g ${attrs}>${coverage}${shell}${details}${variantAccent(geometry, accentColor)}</g>`;
}

/**
 * Cover the animated arm strokes with garment-owned sleeves.
 *
 * Back/front are returned separately because the body and held weapon sit
 * between them in the character layer stack.
 */
export function renderTorsoSleeves(
  family: TorsoFamily,
  primaryColor: string,
  accentColor: string,
  variant: number,
  context: GearFitContext,
  anchors: GhostPoseAnchors,
  semanticAttrs = '',
): GearSleeveLayers {
  const geometry = sleeveRecipe(
    bodyRigForGear(context),
    family,
    variant,
    anchors,
  );
  const outline = family === 'plate'
    ? GEAR_COLORS.plateDarkOutline
    : family === 'tunic' || family === 'vest'
      ? GEAR_COLORS.leatherOutline
      : GEAR_COLORS.outline;

  const sleeve = (
    region: 'back-arm' | 'front-arm',
    d: string,
    cuff: { x: number; y: number },
    opacity: number,
  ): string => {
    const cuffX = Number(cuff.x.toFixed(2));
    const cuffY = Number(cuff.y.toFixed(2));
    const cuffRadius = Number((geometry.width * 0.46).toFixed(2));
    return `<g ${semanticAttrs} data-gear-recipe="${family}-sleeve" data-gear-shape="${context.shape}" data-gear-fit-mode="anchored" data-gear-variant-tier="${geometry.variantTier}" data-gear-coverage-region="${region}" data-gear-coverage-mode="wrap" opacity="${opacity}">
    <path d="${d}" fill="none" stroke="${outline}" stroke-width="${geometry.width + 2.2}" stroke-linecap="round" />
    <path d="${d}" fill="none" stroke="${primaryColor}" stroke-width="${geometry.width}" stroke-linecap="round" />
    <circle cx="${cuffX}" cy="${cuffY}" r="${cuffRadius}" fill="${primaryColor}" stroke="${accentColor}" stroke-width="1" />
  </g>`;
  };

  return {
    back: sleeve('back-arm', geometry.back, geometry.backCuff, 0.94),
    front: sleeve('front-arm', geometry.front, geometry.frontCuff, 1),
  };
}

function renderLegFamily(
  family: LegFamily,
  primaryColor: string,
  accentColor: string,
  variant: number,
  context: GearFitContext,
  semanticAttrs = '',
): string {
  const geometry = legRecipe(bodyRigForGear(context), family, variant);
  const attrs = `${semanticAttrs} ${fitAttrs(family, context, geometry.fitMode, geometry.variantTier)}`.trim();
  const outline = family === 'plate' ? GEAR_COLORS.plateDarkOutline : GEAR_COLORS.outline;
  const strokeWidth = family === 'leggings' ? 0.8 : 1.1;
  const coverage = renderBodyWrapCoverage(
    geometry.coverage,
    darken(primaryColor, family === 'plate' ? 0.7 : 0.8),
    outline,
    family === 'plate' ? 1.5 : 1.15,
  );
  const shells = family === 'skirt'
    ? path(geometry.skirt, primaryColor, outline, 1.2)
    : path(geometry.back, primaryColor, outline, strokeWidth, 0.92)
      + path(geometry.front, primaryColor, outline, strokeWidth);
  const seam = `<path d="${geometry.seam}" fill="none" stroke="${accentColor}" stroke-width="${geometry.variantTier > 2 ? 1.5 : 1}" opacity="${geometry.variantTier > 0 ? 0.72 : 0.42}" ${NON_SCALING_STROKE} />`;
  const variantDetail = geometry.variantTier > 0
    ? `<path data-gear-variant-accent="leg-cuff" d="M${geometry.centerX - 18},${geometry.bottomY - 3} Q${geometry.centerX},${geometry.bottomY} ${geometry.centerX + 18},${geometry.bottomY - 3}" fill="none" stroke="${accentColor}" stroke-width="1.2" opacity="0.72" ${NON_SCALING_STROKE} />`
    : '';
  return `<g ${attrs}>${coverage}${shells}${seam}${variantDetail}</g>`;
}

function renderHeadFamily(
  family: HeadFamily,
  primaryColor: string,
  accentColor: string,
  variant: number,
  context: GearFitContext,
  semanticAttrs = '',
  legacyHeadMarker = '',
): string {
  const geometry = headRecipe(bodyRigForGear(context), family, variant);
  const attrs = `${semanticAttrs} ${fitAttrs(family, context, geometry.fitMode, geometry.variantTier)} ${legacyHeadMarker}`.trim();
  const openShell = family === 'hood' || family === 'helm';
  const antlers = family === 'antlers';
  const coverage = geometry.coverage
    ? renderHeadWrapCoverage(
      geometry.coverage,
      darken(primaryColor, family === 'helm' ? 0.72 : 0.82),
      GEAR_COLORS.outline,
    )
    : '';
  const shell = `<path d="${geometry.shell}" fill="${antlers ? 'none' : primaryColor}" fill-rule="${openShell ? 'evenodd' : 'nonzero'}" clip-rule="${openShell ? 'evenodd' : 'nonzero'}" stroke="${GEAR_COLORS.outline}" stroke-width="${family === 'helm' ? 1.8 : 1.35}" stroke-linecap="round" stroke-linejoin="round" ${NON_SCALING_STROKE} />`;
  const trim = geometry.trim
    ? `<path d="${geometry.trim}" fill="none" stroke="${accentColor}" stroke-width="${family === 'mask' ? 1.8 : 1.5}" opacity="0.88" ${NON_SCALING_STROKE} />`
    : '';
  const accent = geometry.accent
    ? `<path ${geometry.variantTier > 0 ? `data-gear-variant-accent="${family}-detail"` : `data-gear-detail="${family}-detail"`} d="${geometry.accent}" fill="none" stroke="${accentColor}" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" opacity="0.84" ${NON_SCALING_STROKE} />`
    : '';
  const variantAccent = geometry.variantAccent
    ? `<path data-gear-variant-accent="${family}-tier-${geometry.variantTier}" d="${geometry.variantAccent}" fill="none" stroke="${accentColor}" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round" opacity="0.78" ${NON_SCALING_STROKE} />`
    : '';
  return `<g ${attrs}>${coverage}${shell}${trim}${accent}${variantAccent}</g>`;
}

function renderCloak(
  primaryColor: string,
  accentColor: string,
  variant: number,
  context: GearFitContext,
  semanticAttrs = '',
): string {
  const geometry = cloakRecipe(bodyRigForGear(context), variant);
  const attrs = `${semanticAttrs} ${fitAttrs('cloak', context, geometry.fitMode, geometry.variantTier)}`.trim();
  const variantAccent = geometry.variantTier > 0
    ? `<path data-gear-variant-accent="cloak-panel" d="M${geometry.centerX},57 L${geometry.centerX},105" fill="none" stroke="${accentColor}" stroke-width="1.4" opacity="0.54" ${NON_SCALING_STROKE} />`
    : '';
  return `<g ${attrs}>`
    + path(geometry.shell, primaryColor, GEAR_COLORS.outline, 1.2, 0.96)
    + `<path d="${geometry.hem}" fill="none" stroke="${accentColor}" stroke-width="2.4" opacity="0.78" ${NON_SCALING_STROKE} />`
    + variantAccent
    + '</g>';
}

function renderNeckFamily(
  family: NeckFamily,
  primaryColor: string,
  accentColor: string,
  variant: number,
  context: GearFitContext,
  semanticAttrs = '',
): string {
  const geometry = neckRecipe(bodyRigForGear(context), family, variant);
  const attrs = `${semanticAttrs} ${fitAttrs(family, context, geometry.fitMode, geometry.variantTier)} data-gear-fit="neck-${family}"`.trim();
  const cx = geometry.centerX;
  const y = geometry.ornamentY;
  let ornament = '';
  switch (family) {
  case 'pendant':
    ornament = `<circle cx="${cx}" cy="${y}" r="${3.3 + geometry.variantTier * 0.12}" fill="${primaryColor}" stroke="${accentColor}" stroke-width="1" ${NON_SCALING_STROKE} />`;
    break;
  case 'amulet':
    ornament = `<path d="M${cx - 5},${y - 2} L${cx + 5},${y - 2} L${cx + 3},${y + 7} L${cx - 3},${y + 7} Z" fill="${primaryColor}" stroke="${accentColor}" stroke-width="1" ${NON_SCALING_STROKE} />`;
    break;
  case 'necklace':
    ornament = `<g fill="${primaryColor}"><circle cx="${cx - 7}" cy="${y - 2}" r="1.6" /><circle cx="${cx}" cy="${y}" r="2" /><circle cx="${cx + 7}" cy="${y - 2}" r="1.6" /></g>`;
    break;
  case 'ring':
    ornament = `<circle cx="${cx}" cy="${y}" r="4" fill="none" stroke="${primaryColor}" stroke-width="2" ${NON_SCALING_STROKE} /><circle cx="${cx}" cy="${y - 4}" r="1.5" fill="${accentColor}" />`;
    break;
  case 'trinket':
    ornament = `<rect x="${cx - 5}" y="${y - 3}" width="10" height="8" rx="2" fill="${primaryColor}" stroke="${accentColor}" stroke-width="1" ${NON_SCALING_STROKE} />`;
    break;
  }
  const variantAccent = geometry.variantTier > 2
    ? `<circle data-gear-variant-accent="neck-glint" cx="${cx + 2}" cy="${y - 1}" r="1" fill="#ffffff" opacity="0.72" />`
    : '';
  return `<g ${attrs}><path d="${geometry.chain}" fill="none" stroke="${accentColor}" stroke-width="1.5" ${NON_SCALING_STROKE} />${ornament}${variantAccent}</g>`;
}

function renderShoulders(
  rig: BodyRig,
  color: string,
  heavy: boolean,
): string {
  const geometry = shoulderRecipe(rig, heavy);
  return path(geometry.back, color, GEAR_COLORS.plateDarkOutline, heavy ? 1.4 : 1.1)
    + path(geometry.front, color, GEAR_COLORS.plateDarkOutline, heavy ? 1.5 : 1.1);
}

function renderPlate(
  color: string,
  slots: EquipmentSlots,
  context: GearFitContext,
): MaterialLayers {
  const back: string[] = [];
  const body: string[] = [];
  const accent = darken(color, 0.62);
  const rig = bodyRigForGear(context);

  if (slots.back > 0) back.push(renderCloak(slots.back === 2 ? '#fff' : '#900', accent, slots.back - 1, context));
  if (slots.legs > 0) body.push(renderLegFamily(slots.legs === 1 ? 'plate' : 'mail', slots.legs === 1 ? GEAR_COLORS.plateSecondary : color, accent, slots.legs - 1, context));
  if (slots.chest > 0) body.push(renderTorsoFamily(slots.chest === 1 ? 'plate' : 'mail', color, accent, slots.chest - 1, context));
  if (slots.shoulders > 0) body.push(renderShoulders(rig, color, slots.shoulders === 1));

  return { back, body };
}

function renderRobe(
  color: string,
  slots: EquipmentSlots,
  context: GearFitContext,
): MaterialLayers {
  const back: string[] = [];
  const body: string[] = [];
  if (slots.back > 0) back.push(renderCloak(GEAR_COLORS.robeMantle, darken(GEAR_COLORS.robeMantle, 0.68), slots.back - 1, context));
  if (slots.chest > 0) body.push(renderTorsoFamily('robe', color, GEAR_COLORS.robeSecondary, slots.chest - 1, context));
  return { back, body };
}

function isRoguePalette(color: string): boolean {
  const normalized = color.toLowerCase();
  return normalized === '#222222' || normalized === '#1c1c26';
}

function renderLeather(
  color: string,
  slots: EquipmentSlots,
  context: GearFitContext,
): MaterialLayers {
  const back: string[] = [];
  const body: string[] = [];
  const rogue = isRoguePalette(color);
  const secondary = rogue ? GEAR_COLORS.rogueSecondary : GEAR_COLORS.leatherSecondary;

  if (slots.back > 0 && rogue) back.push(renderCloak(secondary, GEAR_COLORS.rogueSecondary, slots.back - 1, context));
  if (slots.legs > 0) body.push(renderLegFamily('leggings', secondary, darken(secondary, 0.65), slots.legs - 1, context));
  if (slots.chest > 0) body.push(renderTorsoFamily('tunic', color, secondary, slots.chest - 1, context));
  return { back, body };
}

function renderCommonSlots(
  slots: EquipmentSlots,
  context: GearFitContext,
): string {
  const body: string[] = [];
  const rig = bodyRigForGear(context);

  if (slots.neck === 1) body.push(renderNeckFamily('pendant', '#b73434', '#d6b55c', 0, context));
  if (slots.neck === 2) body.push(renderNeckFamily('necklace', '#ffd76b', '#ffd76b', 1, context));

  const waist = rig.torso.waist;
  if (slots.waist === 1) {
    body.push(`<path data-gear-recipe="belt" data-gear-shape="${context.shape}" d="M${waist.left},${waist.y + 3} Q${rig.centerX},${waist.y + 6} ${waist.right},${waist.y + 3}" fill="none" stroke="#3e2723" stroke-width="5" ${NON_SCALING_STROKE} />`);
  }
  if (slots.waist === 2) {
    body.push(`<path data-gear-recipe="sash" data-gear-shape="${context.shape}" d="M${waist.left},${waist.y} L${waist.right},${waist.y} L${waist.right + 2},${waist.y + 9} Q${rig.centerX},${waist.y + 12} ${waist.left - 2},${waist.y + 9} Z" fill="#483d8b" />`);
  }

  if (slots.classItem === 1) {
    body.push(`<g data-gear-fit="left-hip-orb"><path d="M${waist.left + 4},${waist.y + 4} Q${waist.left - 4},${waist.y + 4} ${waist.left - 5},${waist.y - 1}" fill="none" stroke="#5e4632" stroke-width="2" ${NON_SCALING_STROKE} /><circle cx="${waist.left - 6}" cy="${waist.y - 3}" r="5" fill="#00ffff" stroke="#244c56" stroke-width="1" /></g>`);
  }
  if (slots.classItem === 2) {
    const bookX = Math.min(120, waist.right + 8);
    body.push(`<g data-gear-fit="right-hip-book"><path d="M${waist.right - 4},${waist.y + 5} Q${bookX - 2},${waist.y + 2} ${bookX + 4},${waist.y + 7}" fill="none" stroke="#5e4632" stroke-width="2.5" ${NON_SCALING_STROKE} /><rect x="${bookX}" y="${waist.y + 2}" width="14" height="19" rx="1.5" fill="#8b4513" transform="rotate(-10 ${bookX} ${waist.y + 2})" /></g>`);
  }

  return body.join('');
}

function renderHead(
  equipment: EquipmentConfig,
  context: GearFitContext,
): string {
  const { color, helmet } = equipment;
  if (helmet === 'hood') return renderHeadFamily('hood', color, darken(color, 0.55), 0, context, '', 'data-gear-head="hood"');
  if (helmet === 'helm') return renderHeadFamily('helm', color, darken(color, 0.55), 0, context, '', 'data-gear-head="helm"');
  if (helmet === 'bandana') return renderHeadFamily('headband', color, darken(color, 0.65), 0, context, '', 'data-gear-head="bandana"');
  return '';
}

/** Render authored legacy material silhouettes against the selected body rig. */
export function renderGearSilhouettes(
  equipment: EquipmentConfig,
  context: GearFitContext = DEFAULT_GEAR_FIT,
): GearSilhouetteLayers {
  const slots = equipment.slots ?? EMPTY_EQUIPMENT_SLOTS;
  const material = equipment.type === 'plate'
    ? renderPlate(equipment.color, slots, context)
    : equipment.type === 'robe'
      ? renderRobe(equipment.color, slots, context)
      : renderLeather(equipment.color, slots, context);

  return {
    back: material.back.join(''),
    body: material.body.join('') + renderCommonSlots(slots, context),
    head: renderHead(equipment, context),
  };
}

type SlotFamilyRenderer = (
  d: ItemEquipmentVisualDescriptor,
  context: GearFitContext,
) => string;

const RESOLVED_GEAR_FAMILIES: Partial<Record<EquipmentSchemaSlot, Record<string, SlotFamilyRenderer>>> = {
  head: {
    hood: (d, context) => renderHeadFamily('hood', d.primaryColor, d.accentColor ?? darken(d.primaryColor, 0.55), d.variant, context, equipAttrs(d)),
    helm: (d, context) => renderHeadFamily('helm', d.primaryColor, d.accentColor ?? darken(d.primaryColor, 0.55), d.variant, context, equipAttrs(d)),
    hat: (d, context) => renderHeadFamily('hat', d.primaryColor, d.accentColor ?? darken(d.primaryColor, 0.7), d.variant, context, equipAttrs(d)),
    crown: (d, context) => renderHeadFamily('crown', d.primaryColor, d.accentColor ?? d.primaryColor, d.variant, context, equipAttrs(d)),
    headband: (d, context) => renderHeadFamily('headband', d.primaryColor, d.accentColor ?? d.primaryColor, d.variant, context, equipAttrs(d)),
    mask: (d, context) => renderHeadFamily('mask', d.primaryColor, d.accentColor ?? darken(d.primaryColor, 0.65), d.variant, context, equipAttrs(d)),
    antlers: (d, context) => renderHeadFamily('antlers', d.primaryColor, d.accentColor ?? d.primaryColor, d.variant, context, equipAttrs(d)),
    ...flameHeadFamilies(equipAttrs),
  },
  chest: {
    plate: (d, context) => renderTorsoFamily('plate', d.primaryColor, d.accentColor ?? darken(d.primaryColor, 0.7), d.variant, context, equipAttrs(d)),
    mail: (d, context) => renderTorsoFamily('mail', d.primaryColor, d.accentColor ?? darken(d.primaryColor, 0.65), d.variant, context, equipAttrs(d)),
    robe: (d, context) => renderTorsoFamily('robe', d.primaryColor, d.accentColor ?? darken(d.primaryColor, 0.7), d.variant, context, equipAttrs(d)),
    tunic: (d, context) => renderTorsoFamily('tunic', d.primaryColor, d.accentColor ?? darken(d.primaryColor, 0.68), d.variant, context, equipAttrs(d)),
    vest: (d, context) => renderTorsoFamily('vest', d.primaryColor, d.accentColor ?? darken(d.primaryColor, 0.7), d.variant, context, equipAttrs(d)),
    shirt: (d, context) => renderTorsoFamily('shirt', d.primaryColor, d.accentColor ?? darken(d.primaryColor, 0.72), d.variant, context, equipAttrs(d)),
  },
  legs: {
    plate: (d, context) => renderLegFamily('plate', d.primaryColor, d.accentColor ?? darken(d.primaryColor, 0.7), d.variant, context, equipAttrs(d)),
    mail: (d, context) => renderLegFamily('mail', d.primaryColor, d.accentColor ?? darken(d.primaryColor, 0.7), d.variant, context, equipAttrs(d)),
    leggings: (d, context) => renderLegFamily('leggings', d.primaryColor, d.accentColor ?? darken(d.primaryColor, 0.7), d.variant, context, equipAttrs(d)),
    pants: (d, context) => renderLegFamily('pants', d.primaryColor, d.accentColor ?? darken(d.primaryColor, 0.7), d.variant, context, equipAttrs(d)),
    skirt: (d, context) => renderLegFamily('skirt', d.primaryColor, d.accentColor ?? darken(d.primaryColor, 0.7), d.variant, context, equipAttrs(d)),
  },
  back: {
    cloak: (d, context) => renderCloak(d.primaryColor, d.accentColor ?? darken(d.primaryColor, 0.72), d.variant, context, equipAttrs(d)),
  },
  neck: {
    pendant: (d, context) => renderNeckFamily('pendant', d.primaryColor, d.accentColor ?? d.primaryColor, d.variant, context, equipAttrs(d)),
    amulet: (d, context) => renderNeckFamily('amulet', d.primaryColor, d.accentColor ?? d.primaryColor, d.variant, context, equipAttrs(d)),
    necklace: (d, context) => renderNeckFamily('necklace', d.primaryColor, d.accentColor ?? d.primaryColor, d.variant, context, equipAttrs(d)),
    ring: (d, context) => renderNeckFamily('ring', d.primaryColor, d.accentColor ?? d.primaryColor, d.variant, context, equipAttrs(d)),
    trinket: (d, context) => renderNeckFamily('trinket', d.primaryColor, d.accentColor ?? d.primaryColor, d.variant, context, equipAttrs(d)),
  },
};

/**
 * Render a non-anchored slot from semantic item data and the selected body rig.
 * Unknown families fail closed; no content-level coordinate fallback exists.
 */
export function renderResolvedSlotSilhouette(
  d: ItemEquipmentVisualDescriptor,
  context: GearFitContext = DEFAULT_GEAR_FIT,
): string {
  const families = RESOLVED_GEAR_FAMILIES[d.schemaSlot];
  if (!families) return '';
  const renderer = families[d.family];
  if (!renderer) return '';
  return renderer(d, context);
}

export const SUPPORTED_GEAR_FAMILIES: Readonly<Partial<Record<EquipmentSchemaSlot, readonly string[]>>> = Object.fromEntries(
  Object.entries(RESOLVED_GEAR_FAMILIES).map(([slot, families]) => [slot, Object.keys(families as Record<string, SlotFamilyRenderer>)]),
) as Readonly<Partial<Record<EquipmentSchemaSlot, readonly string[]>>>;
